import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  number,
  oneOfType,
  string,
  object,
  array,
  bool,
  func,
} from 'prop-types';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import { pd, TextView, PhoenixThemeProvider } from '@airasia-phoenix/web';
import { formatShortDate } from '@airasia-common/libraries/datetime';
import Styled from '../../utils/Styled';
import Image from '../Image';
import Wrapper from '../Wrapper';
import Row from '../Container/Row';
import { BodyText, CaptionText } from '../Text/Heading';
import LOBIcon from '../Icon/index';
import { AmenitiesType } from '../Amenity';
import { ICON_RATES_AMENITIES } from '../../utils/constants';

const color = pd.sys.colour;
const font = pd.sys.typescale;

const CardContainer = Styled(Wrapper, {
  bg: 'white',
});

const HrLine = styled.hr`
  width: 100%;
  color: ${({ theme }) => theme.colors.greyLight};
`;

const SummaryWrapper = Styled(Wrapper, {
  display: 'block',
  width: '342px',
  border: `1px solid ${pd.sys.colour.outline_low.color()}`,
  borderRadius: '16px',
  paddingLeft: '24px',
  paddingTop: '12px',
  paddingBottom: '10px',
  paddingRight: '24px',
});

const ImageWrapper = Styled(Wrapper, {
  flex: 1,
  borderRadius: '4px',
});

const InfoRightColumn = Styled(Wrapper, {
  width: '100%',
  pl: 4,
  alignItems: 'flex-end',
  textAlign: 'left',
});

const Icon = styled(LOBIcon)`
  margin-right: 4px;
  cursor: pointer;
`;

const amenitiesStyle = {
  display: 'flex',
  gap: '8px',
  marginTop: '8px',
  fontSize: '12px',
  color: pd.sys.colour.content_secondary.color(),
};

export const HotelSummary = ({
  imageUrl,
  hotelName,
  fromDate,
  toDate,
  roomsDescription,
  roomName,
  roomsCount: roomsCountText,
  totalNights,
  className,
  imageStyle,
  isBogoPromoEnabled,
  discountId,
  hotelNameStyle,
  dateRangeStyle,
  roomsDescriptionStyle,
  showDevive,
  viewAmenitiesType,
  onImageLoad,
  move,
  isRM9PromoEnabled,
  rate,
  hotelContent,
  isShowHotelContent,
  bedGroups,
  rateAmenitiesForSummary,
  onClick,
}) => {
  const [AmenitiesDisplayType, setAmenitiesDisplayType] = useState(
    AmenitiesType.ONE_COLUMN_FULL_WIDTH
  );

  const shortAmenity =
    rateAmenitiesForSummary?.amenities.length >= 1 &&
    rateAmenitiesForSummary?.amenities.slice(0, 3);
  const Amenity1 = shortAmenity[0];
  const Amenity2 = shortAmenity[1];
  const Amenity3 = shortAmenity[2];

  useEffect(() => {
    if (viewAmenitiesType && viewAmenitiesType !== AmenitiesDisplayType) {
      if (Object.values(AmenitiesType).indexOf(viewAmenitiesType) !== -1) {
        setAmenitiesDisplayType(viewAmenitiesType);
      }
    }
  }, [AmenitiesDisplayType, viewAmenitiesType]);

  const address = hotelContent?.address;
  const fullAddress = [
    address?.line_1,
    address?.line_2,
    address?.city !== address?.state_province_name ? address.city : null,
    address?.postal_code,
    address?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const normalizeNumber = number => {
    if (!number) return null;
    return number.startsWith('+') ? number : `+${number}`;
  };

  const phone = normalizeNumber(hotelContent?.phone);

  let contactInfo = null;
  if (phone) {
    contactInfo = phone;
  } else {
    contactInfo = null;
  }

  return (
    <PhoenixThemeProvider>
      <SummaryWrapper
        height={
          rateAmenitiesForSummary?.amenities.length > 0 ? 'unset' : '130px'
        }
      >
        <Row justifyContent="space-between" mb={16}>
          <TextView
            font={font.body_text_medium}
            color={pd.sys.colour.content_primary.color()}
            variant={'span'}
          >
            {translate('common.hotelLabel')}
          </TextView>

          <Wrapper>
            <TextView
              style={{ cursor: 'pointer' }}
              font={font.sub_body_text}
              color={pd.sys.colour.interactive_link.color()}
              onClick={onClick}
              variant={'span'}
            >
              {translate('common.changeHotel')}
            </TextView>
          </Wrapper>
        </Row>
        <Row margin={1}>
          <ImageWrapper>
            {imageUrl && (
              <Image
                src={imageUrl}
                height={76}
                width={76}
                borderRadius="8px"
                className="hotel-summary-image"
                onLoad={onImageLoad}
                {...imageStyle}
              />
            )}
          </ImageWrapper>
          <InfoRightColumn>
            <BodyText
              className="hotel-name"
              numberOfLines={1}
              style={{
                marginTop: '2px',
                width: '252px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineBreak: 'anywhere',
                lineHeight: '18px',
              }}
              fontSize={14}
              color={pd.sys.colour.content_primary.color()}
              {...hotelNameStyle}
            >
              {hotelName}
            </BodyText>

            <CaptionText
              style={{ marginTop: '2px', lineHeight: '16px' }}
              color={pd.sys.colour.content_secondary.color()}
              font={font.caption_text}
              {...dateRangeStyle}
            >
              {translate('tripSummary.totalNights', {
                fromDate: formatShortDate(fromDate),
                toDate: formatShortDate(toDate),
                smart_count: +totalNights,
              })}
            </CaptionText>

            {roomsDescription && (
              <TextView
                className="room-descriptions"
                numberOfLines={1}
                style={{
                  marginTop: '2px',
                  lineHeight: '16px',
                  overflow: 'hidden',
                }}
                font={font.caption_text}
                color={pd.sys.colour.content_secondary.color()}
                {...roomsDescriptionStyle}
              >
                {roomsDescription}
                {bedGroups && ` (${bedGroups})`}
                {roomsCountText >= 1 ? ` (${roomsCountText} room)` : ''}
              </TextView>
            )}
          </InfoRightColumn>
        </Row>
        <div style={amenitiesStyle}>
          <span>
            <Icon icon={ICON_RATES_AMENITIES[Amenity1?.id]} fontsize="normal" />
          </span>
          <span style={{ fontWeight: 400 }}>{Amenity1?.name}</span>
          <span>
            <Icon icon={ICON_RATES_AMENITIES[Amenity2?.id]} fontsize="normal" />
          </span>
          <span style={{ fontWeight: 400 }}>{Amenity2?.name}</span>
          <span>
            <Icon icon={ICON_RATES_AMENITIES[Amenity3?.id]} fontsize="normal" />
          </span>
          <span style={{ fontWeight: 400 }}>{Amenity3?.name}</span>
        </div>
      </SummaryWrapper>
    </PhoenixThemeProvider>
  );
};

HotelSummary.propTypes = {
  imageUrl: string,
  hotelName: string.isRequired,
  fromDate: string.isRequired,
  toDate: string.isRequired,
  roomsDescription: string,
  roomName: string,
  totalNights: oneOfType([string, number]).isRequired,
  className: string,
  imageStyle: object,
  hotelNameStyle: object,
  dateRangeStyle: object,
  isBogoPromoEnabled: bool,
  discountId: string,
  roomsDescriptionStyle: object,
  roomsCount: oneOfType([string, number]),
  amenities: array,
  showDevive: bool,
  viewAmenitiesType: string,
  onImageLoad: func,
  move: bool,
  isRM9PromoEnabled: bool,
  rate: object,
  hotelContent: object,
  isShowHotelContent: bool,
  bedGroups: string,
  rateAmenitiesForSummary: array,
  onClick: func,
};

const HotelCardNewSummary = props => {
  return (
    <CardContainer>
      <HotelSummary {...props} />
    </CardContainer>
  );
};

export default HotelCardNewSummary;
